import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Lock, Mail, Phone, RefreshCw, LogOut, Inbox, CheckCircle2, AlertCircle, FileEdit, BarChart3 } from "lucide-react";
import ContentEditor from "@/components/admin/ContentEditor";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";
import { useSiteContentState } from "@/context/SiteContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "isi_admin_pwd";

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const { refresh: refreshSiteContent } = useSiteContentState() || {};

  const verifyAndLoad = useCallback(async (pwd) => {
    setAuthLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Password": pwd } });
      sessionStorage.setItem(STORAGE_KEY, pwd);
      setAuthed(true);
      fetchContacts(pwd);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("密码错误");
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        toast.error("验证失败，请稍后再试");
      }
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const fetchContacts = async (pwd) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { "X-Admin-Password": pwd },
      });
      setContacts(res.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        toast.error("加载失败");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setPassword(saved);
      verifyAndLoad(saved);
    }
  }, [verifyAndLoad]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!password) return toast.error("请输入密码");
    verifyAndLoad(password);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPassword("");
    setContacts([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-6">
        <div className="w-full max-w-sm bg-white rounded-sm border border-slate-200 p-10">
          <div className="w-12 h-12 rounded-full bg-orange-100 text-orange-600 grid place-items-center mb-6">
            <Lock size={20} />
          </div>
          <h1 className="font-serif-sc text-2xl font-bold text-slate-900">管理员登录</h1>
          <p className="text-sm text-slate-500 mt-2">请输入管理员密码以管理着陆页</p>
          <form onSubmit={handleLogin} className="mt-8 space-y-6">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display block mb-2">
                密码 · Password
              </label>
              <Input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              disabled={authLoading}
              data-testid="admin-login-button"
              className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full h-12 text-sm font-medium tracking-wide active:scale-95 transition-all disabled:opacity-60"
            >
              {authLoading ? "验证中..." : "登录"}
            </Button>
          </form>
          <a href="/" className="block mt-6 text-center text-xs text-slate-500 hover:text-orange-600">
            ← 返回首页
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif-sc text-lg font-bold text-slate-900">
              iSi<span className="text-orange-600">.</span>
            </span>
            <span className="h-4 w-px bg-slate-300" />
            <span className="text-xs tracking-[0.2em] uppercase text-slate-500 font-display">
              Admin Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="text-slate-700 hover:text-orange-600"
            >
              <LogOut size={14} />
              <span className="ml-2 text-xs">退出</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Tabs defaultValue="inbox" className="w-full">
          <TabsList data-testid="admin-tabs" className="bg-white border border-slate-200 rounded-sm p-1 mb-8">
            <TabsTrigger value="inbox" data-testid="tab-inbox" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-sm px-6 py-2">
              <Inbox size={14} className="mr-2" /> 留言箱
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-sm px-6 py-2">
              <BarChart3 size={14} className="mr-2" /> 流量分析
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-sm px-6 py-2">
              <FileEdit size={14} className="mr-2" /> 内容编辑
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inbox">
            <InboxTab
              contacts={contacts}
              loading={loading}
              onRefresh={() => fetchContacts(password)}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsPanel password={password} />
          </TabsContent>

          <TabsContent value="content">
            <ContentEditor password={password} onSaved={() => refreshSiteContent && refreshSiteContent()} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function InboxTab({ contacts, loading, onRefresh }) {
  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif-sc text-3xl lg:text-4xl font-bold text-slate-900">联系表单留言</h1>
          <p className="text-slate-500 mt-2 text-sm">来自着陆页的所有客户咨询</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          data-testid="admin-refresh-button"
          className="text-slate-700 hover:text-orange-600"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span className="ml-2 text-xs">刷新</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total" value={contacts.length} icon={Inbox} />
        <StatCard label="Email Sent" value={contacts.filter((c) => c.email_sent).length} icon={CheckCircle2} accent="text-emerald-600" />
        <StatCard label="Email Failed" value={contacts.filter((c) => !c.email_sent).length} icon={AlertCircle} accent="text-amber-600" />
        <StatCard label="Today" value={contacts.filter((c) => isToday(c.created_at)).length} icon={Inbox} accent="text-orange-600" />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {contacts.length === 0 ? (
          <div className="py-20 text-center text-slate-400">
            <Inbox size={32} className="mx-auto opacity-40" />
            <p className="mt-4 text-sm">暂时没有留言</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {contacts.map((c) => (
              <div
                key={c.id}
                data-testid={`admin-contact-${c.id}`}
                className="p-6 lg:p-8 hover:bg-slate-50 transition-colors"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif-sc text-lg font-semibold text-slate-900">{c.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-display tracking-wide">
                        {c.subject || "一般咨询"}
                      </span>
                      {c.email_sent ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          邮件已发送
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          邮件未发送
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-600">
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-orange-600">
                        <Mail size={12} /> {c.email}
                      </a>
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-orange-600">
                          <Phone size={12} /> {c.phone}
                        </a>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 font-display tracking-wide whitespace-nowrap">
                    {formatDate(c.created_at)}
                  </div>
                </div>
                <div className="mt-4 p-4 bg-slate-50 border-l-2 border-orange-600 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {c.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display">{label}</span>
        <Icon size={16} className="text-slate-400" />
      </div>
      <div className={`font-display font-bold text-3xl mt-3 tracking-tighter ${accent}`}>{value}</div>
    </div>
  );
}

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.toDateString() === now.toDateString();
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("zh-CN", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
